import { useEffect, useState } from "react";
import axios from "axios";
import { LogOut, Loader2, Mail, Phone, CalendarDays, Users, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const EVENT_LABELS = { juhlat: "Juhlat", kokous: "Kokous tai koulutus", saunailta: "Saunailta", muu: "Muu" };

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fi-FI", { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { username, password }, { withCredentials: true });
      onLogin(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Kirjautuminen epäonnistui");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F7F4EE] px-6">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl border border-[#8A9B87]/20 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.05)]" data-testid="admin-login-form">
        <p className="font-hand text-3xl text-[#8A9B87] -rotate-1">Hengähdys</p>
        <h1 className="mt-2 text-2xl font-light text-[#2E2E2C]">Kirjaudu sisään</h1>
        <label className="mt-6 block text-sm text-[#2E2E2C]/70">
          Käyttäjätunnus
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            autoComplete="username"
            data-testid="admin-username-input"
            className="mt-1 w-full rounded-xl border border-[#8A9B87]/30 bg-[#F7F4EE]/60 px-4 py-3 text-[#2E2E2C] outline-none focus:border-[#8A9B87]"
          />
        </label>
        <label className="mt-4 block text-sm text-[#2E2E2C]/70">
          Salasana
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            data-testid="admin-password-input"
            className="mt-1 w-full rounded-xl border border-[#8A9B87]/30 bg-[#F7F4EE]/60 px-4 py-3 text-[#2E2E2C] outline-none focus:border-[#8A9B87]"
          />
        </label>
        {error && (
          <p className="mt-4 text-sm text-red-600" data-testid="admin-login-error">{error}</p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#8A9B87] px-6 py-3.5 text-sm font-medium text-white transition-colors hover:bg-[#A9805B] disabled:opacity-60"
        >
          {loading && <Loader2 size={16} className="animate-spin" />}
          Kirjaudu
        </button>
      </form>
    </div>
  );
};

const InquiryCard = ({ inq, index }) => (
  <article
    data-testid={`inquiry-card-${index}`}
    className="rounded-2xl border border-[#8A9B87]/15 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.03)]"
  >
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h3 className="text-lg font-medium text-[#2E2E2C]">{inq.name}</h3>
      <span className="text-xs text-[#2E2E2C]/50">{formatDate(inq.created_at)}</span>
    </div>
    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#2E2E2C]/75">
      <span className="inline-flex items-center gap-1.5"><Mail size={14} className="text-[#8A9B87]" /><a href={`mailto:${inq.email}`} className="hover:text-[#8A9B87]">{inq.email}</a></span>
      <span className="inline-flex items-center gap-1.5"><Phone size={14} className="text-[#8A9B87]" /><a href={`tel:${inq.phone}`} className="hover:text-[#8A9B87]">{inq.phone}</a></span>
      <span className="inline-flex items-center gap-1.5"><CalendarDays size={14} className="text-[#8A9B87]" />{inq.preferred_date}</span>
      <span className="inline-flex items-center gap-1.5"><Users size={14} className="text-[#8A9B87]" />{inq.guests} hlö</span>
    </div>
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <span className="rounded-full bg-[#8A9B87]/15 px-3 py-1 text-xs text-[#5d6f5a]">{EVENT_LABELS[inq.event_type] || inq.event_type}</span>
      <span className={`rounded-full px-3 py-1 text-xs ${inq.email_sent ? "bg-[#8A9B87]/15 text-[#5d6f5a]" : "bg-[#A9805B]/15 text-[#8a6a4d]"}`}>
        {inq.email_sent ? "Sähköposti lähetetty" : "Ei sähköpostia"}
      </span>
    </div>
    {inq.message && <p className="mt-4 rounded-xl bg-[#F7F4EE] px-4 py-3 text-sm text-[#2E2E2C]/80 leading-relaxed">{inq.message}</p>}
  </article>
);

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadInquiries = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/inquiries`, { withCredentials: true });
      setInquiries(data.inquiries);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/api/auth/me`, { withCredentials: true })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (user) loadInquiries();
  }, [user]);

  const logout = async () => {
    await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    setUser(null);
    setInquiries([]);
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F7F4EE]">
        <Loader2 size={28} className="animate-spin text-[#8A9B87]" />
      </div>
    );
  }

  if (!user) return <LoginForm onLogin={setUser} />;

  return (
    <div className="min-h-screen bg-[#F7F4EE]" data-testid="admin-dashboard">
      <header className="border-b border-[#8A9B87]/15 bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <div>
            <p className="font-hand text-2xl text-[#8A9B87] -rotate-1">Hengähdys</p>
            <p className="text-xs text-[#2E2E2C]/60">Varauskyselyt — {user.name}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={loadInquiries}
              data-testid="admin-refresh-btn"
              className="flex items-center gap-2 rounded-full border border-[#8A9B87]/30 px-4 py-2 text-sm text-[#2E2E2C]/75 transition-colors hover:bg-[#8A9B87]/10"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Päivitä
            </button>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 rounded-full bg-[#2E2E2C] px-4 py-2 text-sm text-[#F7F4EE] transition-colors hover:bg-[#A9805B]"
            >
              <LogOut size={14} /> Kirjaudu ulos
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-10">
        <h1 className="text-2xl font-light text-[#2E2E2C]">
          Varauskyselyt <span className="text-[#8A9B87]" data-testid="inquiry-count">({inquiries.length})</span>
        </h1>
        <div className="mt-6 space-y-4">
          {loading && inquiries.length === 0 ? (
            <p className="text-sm text-[#2E2E2C]/60">Ladataan…</p>
          ) : inquiries.length === 0 ? (
            <p className="rounded-2xl bg-white p-8 text-center text-sm text-[#2E2E2C]/60" data-testid="no-inquiries">
              Ei vielä varauskyselyitä.
            </p>
          ) : (
            inquiries.map((inq, i) => <InquiryCard key={inq.id} inq={inq} index={i} />)
          )}
        </div>
      </main>
    </div>
  );
}
